import { Box, CircularProgress, Typography } from "@mui/material";
import CustomizedButton from "components/Custom/CustomizedButton";
import DeliveryTripItemListPdfTable, {
  GroupedServiceItem,
} from "components/Table/Sales/BillingNote/DeliveryTripItemList/PDF";
// import DeliveryTripListPdfTable from "components/Table/Sales/BillingNote/DeliveryTripList/PDF";
import PDFLayout from "components/UI/PDF/Layout";
import { UserQuery, useUserQuery } from "generated/general";
import {
  BillingNoteFindUniqueQuery,
  useBillingNoteFindUniqueQuery,
} from "generated/sales";
import { usePDFMultiplePage } from "hooks/use-pdf-multiple-page";
import { ReactInstance, useMemo, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { useQuery } from "@tanstack/react-query";
import { ITEM_VIEW } from "services/AgGrid/ItemAggrid";
import { createGraphQLClientWithMiddleware } from "services/graphqlClient";
import { IPdfInfo } from "types/global";
// import { IDeliveryTripList } from "types/Sales/billing-note";
import { formatDate } from "utils/Date";
import { renderAddressString } from "utils/Formatter/Global";

const BillingNotePDF = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const documentName =
    searchParams.get("type") === "invoice" ? "ใบแจ้งหนี้" : "ใบวางบิล";
  const componentRef = useRef<ReactInstance | null>(null);

  const graphQLClient = createGraphQLClientWithMiddleware("sales");
  const { data, isLoading, isSuccess } =
    useBillingNoteFindUniqueQuery<BillingNoteFindUniqueQuery>(graphQLClient, {
      uniqueInput: {
        id: id ? parseInt(id) : undefined,
      },
    });

  const graphQLClientGeneral = createGraphQLClientWithMiddleware("general");

  const { data: userData } = useUserQuery<UserQuery>(
    graphQLClientGeneral,
    {
      uniqueInput: {
        email: data?.BillingNoteFindUnique.created_by.email,
      },
    },
    {
      enabled: isSuccess,
    },
  );

  const incomeItemUids = useMemo(() => {
    const list = data?.BillingNoteFindUnique?.delivery_trip_item_list ?? [];
    return Array.from(new Set(list.map((i) => i.income_item_unique_id).filter(Boolean))) as string[];
  }, [data?.BillingNoteFindUnique?.delivery_trip_item_list]);

  const graphQLClientWms = createGraphQLClientWithMiddleware("wms");

  const { data: itemMasterData } = useQuery({
    queryKey: ["billingNoteItemMaster", incomeItemUids],
    queryFn: async () => {
      const { ItemsViewAggrid } = await graphQLClientWms.request(ITEM_VIEW, {
        aggridInput: {
          startRow: 0,
          endRow: incomeItemUids.length,
          filterModel: {
            unique_id: { filterType: "set", values: incomeItemUids },
          },
          sortModel: [],
        },
      });
      return ItemsViewAggrid.results as { unique_id: string; name: string }[];
    },
    enabled: isSuccess && incomeItemUids.length > 0,
    staleTime: Infinity,
  });

  const itemNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const item of itemMasterData ?? []) {
      map[item.unique_id] = item.name;
    }
    return map;
  }, [itemMasterData]);

  // Pre-group delivery_trip_item_list by service code before pagination
  const groupedDeliveryTripItems = useMemo<GroupedServiceItem[]>(() => {
    const flatList = data?.BillingNoteFindUnique?.delivery_trip_item_list ?? [];
    if (!flatList.length) return [];

    const map = new Map<string, GroupedServiceItem>();
    for (const item of flatList) {
      const key = item.income_item_unique_id ?? "";
      const existing = map.get(key);
      if (existing) {
        existing.qty += 1;
        existing.amount += item.income_item_pre_vat_amount ?? 0;
      } else {
        map.set(key, {
          income_item_unique_id: key,
          income_item_name: itemNameMap[key] ?? item.income_item_name ?? "",
          qty: 1,
          amount: item.income_item_pre_vat_amount ?? 0,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      a.income_item_unique_id.localeCompare(b.income_item_unique_id),
    );
  }, [data?.BillingNoteFindUnique?.delivery_trip_item_list, itemNameMap]);

  const totalAmount = useMemo(
    () => groupedDeliveryTripItems.reduce((sum, item) => sum + item.amount, 0),
    [groupedDeliveryTripItems],
  );

  // Pass pre-grouped items to pagination hook so each page gets a proper slice
  const pdfData = useMemo(() => {
    if (!data?.BillingNoteFindUnique) return undefined;
    if (!groupedDeliveryTripItems.length) return data.BillingNoteFindUnique;
    return {
      ...data.BillingNoteFindUnique,
      delivery_trip_item_list: groupedDeliveryTripItems,
    };
  }, [data?.BillingNoteFindUnique, groupedDeliveryTripItems]);

  const { paginatedItems, startNumbers } = usePDFMultiplePage(
    pdfData,
    "billing_note", // document type
    139.66, // name and description row width
    640, // max height without footer
    495, // max height with footer
    224.875, // left header minimum height
    247.797, // right header minimum height
    37.313, // left footer minimum height
    87.963, // right footer minimum height
  );

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: data?.BillingNoteFindUnique?.unique_id,
  });

  const highlightSection: IPdfInfo = {
    headers: ["เลขที่เอกสาร", "วันที่ออกเอกสาร", "วันที่ครบกำหนด", "เครดิต"],
    info: [
      data?.BillingNoteFindUnique.unique_id || "-",
      data?.BillingNoteFindUnique.issue_date
        ? formatDate(data?.BillingNoteFindUnique.issue_date)
        : "-",
      data?.BillingNoteFindUnique.due_date
        ? formatDate(data?.BillingNoteFindUnique.due_date)
        : "-",
      data?.BillingNoteFindUnique.credit_day
        ? data.BillingNoteFindUnique.credit_day + " วัน"
        : "-",
    ],
  };

  const leftSection: IPdfInfo = {
    headers: [
      "ลูกค้า",
      "ที่อยู่",
      "เลขประจำตัวผู้เสียภาษี",
      "เบอร์โทรศัพท์@Fax",
      "Email",
    ],
    info: [
      `${data?.BillingNoteFindUnique.customer_unique_id} - ${data?.BillingNoteFindUnique.customer_details.customer_name}`,
      renderAddressString(
        data?.BillingNoteFindUnique.customer_details.billing_address,
        { hideCountry: true },
      ) || "-",
      data?.BillingNoteFindUnique.customer_details.identity_no || "-",
      data?.BillingNoteFindUnique.customer_details.phone +
        "@" +
        data?.BillingNoteFindUnique.customer_details.fax || "-",
      data?.BillingNoteFindUnique.customer_details.email || "-",
    ],
  };

  const rightSection: IPdfInfo = {
    headers: ["ผู้วางบิล", "เบอร์โทรศัพท์", "Email"],
    info: [
      `${userData?.UserFindUnique?.first_name || "-"} ${userData?.UserFindUnique?.last_name || "-"}`,
      userData?.UserFindUnique?.phone || "-",
      userData?.UserFindUnique?.email || "-",
    ],
  };

  if (id && isLoading) {
    return (
      <Box
        sx={{
          height: "calc(100dvh - 176px)",
          marginRight: "260px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mx: "auto" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="h5">{documentName}</Typography>
        <Box display={"flex"} gap={4}>
          <CustomizedButton
            fullWidth
            title="พิมพ์เอกสาร"
            variant="outlined"
            onClick={handlePrint}
          />
        </Box>
      </Box>
      <Box ref={componentRef} sx={{ m: "auto" }}>
        {paginatedItems.map((item, index) => (
          <PDFLayout
            highlightSection={highlightSection}
            leftSection={leftSection}
            rightSection={rightSection}
            name={documentName}
            data={data?.BillingNoteFindUnique}
            summary={index === paginatedItems.length - 1}
            multiplePage={paginatedItems.length > 1}
            page={index + 1}
            allPages={paginatedItems.length}
            documentType="billing_note"
            headerType={data?.BillingNoteFindUnique.company_type}
          >
            <DeliveryTripItemListPdfTable
              itemList={(item as GroupedServiceItem[]) || []}
              startNumber={startNumbers[index]}
              lastTable={index === paginatedItems.length - 1}
              totalAmount={totalAmount}
            />
          </PDFLayout>
        ))}
      </Box>
    </Box>
  );
};

export default BillingNotePDF;
